import { SocketClient } from "cbcore-ts"
import {
    CALL,
    EXTEND,
    IF,
    IS,
    IS_NOT,
    nil,
    NO,
    UIColor,
    UIDialogView,
    UINativeScrollView,
    UIPoint,
    UICore,
    UIRootViewController,
    UIRoute,
    UIRouteChangeDescriptor,
    UIRouteComponent,
    UITextView,
    UIView,
    UIViewBroadcastEvent,
    UIViewController,
    YES
} from "uicore-ts"
import { BottomBarView } from "./BottomBarView"
import { ChartViewController } from "./ChartViewController"
import { ChatGPTViewController } from "./ChatGPTViewController"
import {
    CompanyRelationshipApprovalViewController
} from "./CompanyRelationshipApproval/CompanyRelationshipApprovalViewController"
import {
    CompanyMembershipRequestViewController
} from "./CompanyView/CompanyAffiliation/CompanyAffiliationViewController"
import { CompanyViewController } from "./CompanyView/CompanyViewController"
import { ContractsViewController } from "./ContractsView/ContractsViewController"
import {
    activeCompanyMatches,
    directCreateAffiliationForCompany
} from "./Core/RHCompanyAffiliationStore"
import { RHApplicationMode, RHCore } from "./Core/RHCore"
import { RHUsageEventRuntime } from "./Core/RHUsageEventRuntime"
import { CBDialogViewShower } from "./Custom components/CB/CBDialogViewShower"
import { RHAltModeManager } from "./Custom components/RH/Keyboard navigation/RHAltModeManager"
import { elementAcceptsTyping, RHPageFocusManager } from "./Custom components/RH/Keyboard navigation/RHPageFocusManager"
import { RHColor } from "./Custom components/RH/RHColor"
import { RHBarLinkButton } from "./Custom components/RH/RHBarLinkButton"
import { RHBreadcrumbView, type RHBreadcrumbItem } from "./Custom components/RH/RHBreadcrumbView"
import { RHDataView } from "./Custom components/RH/RHDataView"
import { RHNotificationBadge } from "./Custom components/RH/RHNotificationBadge"
import { RHTooltip } from "./Custom components/RH/RHTooltip"
import { RHTransientBanner } from "./Custom components/RH/RHTransientBanner"
import { RHColors, RHFont } from "./Custom components/RH/Utils_slop/RHDesignTokens"
import { RHTextSanitizer } from "./Custom components/RH/Utils_slop/RHTextSanitizer"
import { isRHNavigationResettable } from "./Custom components/RH/Utils_slop/RHNavigationResettable"
import { EditorViewController } from "./EditorViewController"
import { LogEventViewController } from "./EventLog/LogEventViewController"
import { LogViewController } from "./EventLog/LogViewController"
import { FeedbackViewController } from "./FeedbackViewController"
import { HelpViewController } from "./HelpViewController"
import { InformationViewController } from "./InformationViewController"
import { InternalDropdownSettingsViewController } from "./InternalDropdownSettingsViewController"
import { InternalLanguageSettingsViewController } from "./InternalLanguageSettingsViewController"
import { InternalUsageDataViewController } from "./InternalUsageDataViewController"
import { InvoicePaymentViewController } from "./Invoices/InvoicePayment/InvoicePaymentViewController"
import { InvoicesViewController } from "./Invoices/InvoicesViewController"
import { LanguagesDialogView } from "./LanguagesDialogView"
import { LanguageService } from "./LanguageService"
import { ForgotPasswordViewController } from "./Login/ForgotPasswordViewController"
import { LoginViewController } from "./Login/LoginViewController"
import { RegisterUserViewController } from "./Login/RegisterUserViewController"
import { ResetPasswordViewController } from "./Login/ResetPasswordViewController"
import { NewProcurementPreviewViewController } from "./NewProcurement/NewProcurementPreviewViewController"
import { NewProcurementViewController } from "./NewProcurement/NewProcurementViewController"
import { IncomingOffersViewController } from "./Offers/IncomingOffersViewController"
import { OutgoingOffersViewController } from "./Offers/OutgoingOffersViewController"
import { MyOngoingProcurementsViewController } from "./OngoingProcurements/MyOngoingProcurementsViewController"
import { PublishedProcurementsViewController } from "./OngoingProcurements/PublishedProcurementsViewController"
import { OfferDetailViewController } from "./ProcurementDetails/EvaluateOffers/OfferDetailViewController"
import { ProcurementDetailsViewController } from "./ProcurementDetails/ProcurementDetailsViewController"
import { ProcurementPlanViewController } from "./ProcurementPlan/ProcurementPlanViewController"
import { RHRStatisticsComparisonViewController } from "./RHRStatisticsComparisonViewController"
import { RHRStatisticsViewController } from "./RHRStatisticsViewController"
import {
    authenticatedLandingRouteFromRoute,
    procurementDetailsRouteFromPublishedAmendmentRouteWithApplicationMode,
    RHAuthenticationTemplateRouteTarget,
    routeContainsAuthenticationComponent,
    templateTargetFromRoute
} from "./RootViewControllerRouteRules"
import { ProcurementSearchViewController } from "./Search/ProcurementSearchViewController"
import { SettingsViewController } from "./Settings/SettingsViewController"
import { StatisticsMainViewController } from "./StatisticsMainViewController"
import { SystemAdministrationViewController } from "./SystemAdministrationViewController"
import { AsyncGuardTestViewController } from "./Testing/AsyncGuardTestViewController"
import "uicore-ts/compiledScripts/UILayoutCycleTracer"
import { TopBarView } from "./TopBarView"
import { TreeViewViewController } from "./TreeViewViewController"
import { UserMainViewController } from "./UserMain/UserMainViewController"
import { UserProfileViewController } from "./UserProfileViewController"
import "uicore-ts/compiledScripts/UILayoutDebugger"
import { titleKeyOfViewController } from "./Core/RHViewControllerTitleKey"

RHNotificationBadge.installAttentionIndicatorRenderer()

// Install the RH-styled tooltip as the app-wide shared instance, so
// UITooltip.sharedInstance.attach(...) shows the dark RH tooltip (e.g. the
// "Pakkumusi" offer-list tooltip in the Töös hanked table).
new RHTooltip()


// One screen of the Window Management API's screen details.
interface ScreenDetail {
    availWidth: number
    availHeight: number
    availLeft: number
    availTop: number
    isPrimary: boolean
}


export class RootViewController extends UIRootViewController {
    
    override readonly topBarView: TopBarView = new TopBarView("TopBarView", nil).configuredWithObject({
        titleLabel: { setText: CALL("topBarTitle", "UICore application") }
    }).addedAsSubviewToView(this.view)
    
    override readonly bottomBarView: BottomBarView = new BottomBarView("BottomBarView").configuredWithObject({
        style: { overflow: "hidden" }
    }).addedAsSubviewToView(this.view)
    
    readonly languagesDialogViewController = new UIViewController(new LanguagesDialogView("LanguagesDialogView"))

    readonly breadcrumbView = new RHBreadcrumbView()
    
    private _updateBannerView?: UITextView
    _routeReconciliationGeneration = 0
    _routeHandlingGeneration = 0
    _publishedAmendmentAccessDenialRouteString?: string
    _publishedAmendmentAccessDenialDialogShower?: CBDialogViewShower
    readonly _authenticationComponentNames = [
        LoginViewController.routeComponentName,
        RegisterUserViewController.routeComponentName,
        ForgotPasswordViewController.routeComponentName,
        ResetPasswordViewController.routeComponentName
    ]
    readonly _authenticationLandingComponentName = UserMainViewController.routeComponentName
    readonly _authenticationReturnTargetParameterAllowlist: Record<string, string[]> = {
        [NewProcurementViewController.routeComponentName]: [
            NewProcurementViewController.ParameterIdentifierName.templateId
        ]
    }
    
    
    override contentViewControllers = {
        
        informationViewController: this.lazyViewControllerObjectWithClass(InformationViewController),
        helpViewController: this.lazyViewControllerObjectWithClass(HelpViewController),
        internalDropdownSettingsViewController: this.lazyViewControllerObjectWithClass(
            InternalDropdownSettingsViewController,
            {
                shouldShow: async () =>
                    IS((await SocketClient.AreCBInternalSettingsAvailableForCurrentUser()).result) || YES
            }
        ),
        internalLanguageSettingsViewController: this.lazyViewControllerObjectWithClass(
            InternalLanguageSettingsViewController,
            {
                shouldShow: async () =>
                    IS((await SocketClient.AreCBInternalSettingsAvailableForCurrentUser()).result) || YES
            }
        ),
        mainViewController: this.lazyViewControllerObjectWithClass(StatisticsMainViewController),
        treeViewViewController: this.lazyViewControllerObjectWithClass(TreeViewViewController),
        chartViewController: this.lazyViewControllerObjectWithClass(ChartViewController),
        chatGPTViewController: this.lazyViewControllerObjectWithClass(ChatGPTViewController),
        RHRStatisticsViewController: this.lazyViewControllerObjectWithClass(RHRStatisticsViewController),
        RHRStatisticsComparisonViewController: this.lazyViewControllerObjectWithClass(
            RHRStatisticsComparisonViewController
        ),
        
        LoginViewController: this.lazyViewControllerObjectWithClass(
            LoginViewController,
            { deleteOnUnload: YES }
        ),
        RegisterUserViewController: this.lazyViewControllerObjectWithClass(
            RegisterUserViewController,
            { deleteOnUnload: YES }
        ),
        ForgotPasswordViewController: this.lazyViewControllerObjectWithClass(
            ForgotPasswordViewController,
            { deleteOnUnload: YES }
        ),
        ResetPasswordViewController: this.lazyViewControllerObjectWithClass(
            ResetPasswordViewController,
            { deleteOnUnload: YES }
        ),
        CompanyRelationshipApprovalViewController: this.lazyViewControllerObjectWithClass(
            CompanyRelationshipApprovalViewController,
            { deleteOnUnload: YES }
        ),
        UserMainViewController: this.lazyViewControllerObjectWithClass(UserMainViewController),
        UserProfileViewController: this.lazyViewControllerObjectWithClass(
            UserProfileViewController,
            { deleteOnUnload: YES }
        ),
        ProcurementSearchViewController: this.lazyViewControllerObjectWithClass(ProcurementSearchViewController),
        NewProcurementViewController: this.lazyViewControllerObjectWithClass(
            NewProcurementViewController,
            { deleteOnUnload: YES }
        ),
        NewProcurementPreviewViewController: this.lazyViewControllerObjectWithClass(
            NewProcurementPreviewViewController,
            { deleteOnUnload: YES }
        ),
        
        PublishedProcurementsViewController: this.lazyViewControllerObjectWithClass(
            PublishedProcurementsViewController
        ),
        OngoingProcurementsViewController: this.lazyViewControllerObjectWithClass(MyOngoingProcurementsViewController),
        ProcurementDetailsViewController: this.lazyViewControllerObjectWithClass(ProcurementDetailsViewController),
        
        IncomingOffersViewController: this.lazyViewControllerObjectWithClass(IncomingOffersViewController),
        
        OutgoingOffersViewController: this.lazyViewControllerObjectWithClass(OutgoingOffersViewController),
        
        ProcurementPlanViewController: this.lazyViewControllerObjectWithClass(ProcurementPlanViewController),
        DealsViewController: this.lazyViewControllerObjectWithClass(ContractsViewController),
        
        // Registered before CompanyViewController: when a route carries both
        // the "account" and "company" components (Settings' Ettevõte tab),
        // findAsyncSequential must match SettingsViewController first so it
        // hosts CompanyViewController as a child rather than the reverse.
        SettingsViewController: this.lazyViewControllerObjectWithClass(
            SettingsViewController,
            { deleteOnUnload: YES }
        ),
        CompanyViewController: this.lazyViewControllerObjectWithClass(CompanyViewController),
        
        LogViewController: this.lazyViewControllerObjectWithClass(LogViewController),
        
        internalUsageDataViewController: this.lazyViewControllerObjectWithClass(InternalUsageDataViewController),
        
        administrationViewController: this.lazyViewControllerObjectWithClass(SystemAdministrationViewController),

        InvoicesViewController: this.lazyViewControllerObjectWithClass(InvoicesViewController),
        
        asyncGuardTestViewController: this.lazyViewControllerObjectWithClass(
            AsyncGuardTestViewController,
            { deleteOnUnload: YES }
        )
        
    }
    
    // uicore-ts UIRootViewController._detailsDialogView.dismiss discards the
    // immutable route returned by routeByRemovingComponentNamed, so dismissing a
    // details dialog by tapping the backdrop leaves the VC's component in the
    // route. The next button press then computes an identical route — a no-op —
    // and the dialog never reopens. Overridden here to thread the route through
    // each removal. Remove once fixed upstream in uicore-ts.
    // The backdrop fills the viewport height (UIDialogView sizes itself to the
    // root view frame, not the dialog content). A details dialog taller than the
    // viewport — e.g. OfferDetailViewController on a non-maximised window — then
    // overflows with no way to reach its footer (the "Sulge" button), because the
    // page itself does not scroll. Making the backdrop scroll its own overflow is
    // the standard modal-overlay-scroll behaviour and keeps the whole dialog,
    // including the footer, reachable on short screens. Short dialogs show no
    // scrollbar.
    override _detailsDialogView: UIDialogView = new UIDialogView(this.view.elementID + "DetailsDialogView")
        .configuredWithObject({
            style: {
                overflowY: "auto"
            },
            dismiss: EXTEND(() => {
                let route = UIRoute.currentRoute
                this.detailsViewControllers.allValues.forEach(
                    value => route = route.routeByRemovingComponentNamed(value.class.routeComponentName)
                )
                route.apply()
            })
        })
    
    override detailsViewControllers = {
        
        FeedbackViewController: this.lazyViewControllerObjectWithClass(FeedbackViewController),
        LogEventViewController: this.lazyViewControllerObjectWithClass(LogEventViewController),
        OfferDetailViewController: this.lazyViewControllerObjectWithClass(
            OfferDetailViewController,
            {
                deleteOnLogout: YES,
                dismissesOnEscape: YES
            }
        ),
        CompanyMembershipRequestViewController: this.lazyViewControllerObjectWithClass(
            CompanyMembershipRequestViewController,
            {
                deleteOnLogout: YES,
                deleteOnUnload: YES,
                dismissesOnEscape: YES
            }
        ),
        
        InvoicePaymentViewController: this.lazyViewControllerObjectWithClass(
            InvoicePaymentViewController,
            {
                dismissesOnEscape: YES
            }
        )
        
    }
    
    private editor?: EditorViewController
    private editorWindow?: WindowProxy
    
    
    
    constructor(view: UIView) {
        
        super(view)

        // Keep clientWidth stable when short pages sit exactly on the vertical
        // overflow boundary; otherwise page-scale calculation can alternate
        // between scrollbar and no-scrollbar widths on every layout pass.
        UIView.injectCSS(
            "html { scrollbar-gutter: stable; }",
            "rh-stable-root-scrollbar-gutter"
        )
        
        RHCore.initIfNeededWithViewCore(this.view.core)
        UIRoute.shouldApplyRouteChange = routeChangeDescriptor => this._routeChangeShouldApply(routeChangeDescriptor)

        const topBarFocusableItems = this.topBarView._focusManager.focusableItems
        this.topBarView._focusManager.focusableItems = () => [
            ...topBarFocusableItems(),
            this.breadcrumbView
        ]

        UIView.layoutPassMaxIterations = 10
        UIView.layoutCycleThreshold = 15
        
        this.backgroundView.style.background = ""
        this.backgroundView.backgroundColor = RHColors.pageBackground
        this.view.backgroundColor = RHColors.pageBackground
        this.view.style.fontFamily = RHFont.familyBase
        this.view.style.fontSize = RHFont.sizeMedium
        this.view.style.lineHeight = RHFont.lineHeightBase
        
        UITextView.defaultTextColor = RHColor.textBlack
        UITextView.attentionRequiredColor = RHColor.redColor
        UITextView.notificationTextColor = RHColor.redColor
        
        document.addEventListener("keydown", event => {
            
            if (["Escape"].contains(
                event.key) && (document.activeElement == document.body || !document.activeElement)) {
                CBDialogViewShower.currentDialogViewShower?.dialogView.dismiss()
                const activeDetailsLazyObject = this.detailsViewControllers.allValues.find(
                    lazyObject => lazyObject.isInitialized && lazyObject.instance === this._detailsViewController
                )
                if (activeDetailsLazyObject?.dismissesOnEscape) {
                    this._detailsDialogView.dismiss()
                }
            }
            
        })
        
        // Global Tab interceptor — gives RHPageFocusManager full control over
        // Tab navigation at all times, regardless of what element has focus.
        document.addEventListener("keydown", event => {
            if (event.key !== "Tab") {
                return
            }
            const handled = RHPageFocusManager.shared.handleTabEvent(event)
            if (handled) {
                event.preventDefault()
                event.stopPropagation()
            }
        })
        
        
        // Alt mode: activate on Alt keydown, deactivate on Alt keyup,
        // route letter keys to the alt mode manager while active.
        document.addEventListener("keydown", event => {
            if (elementAcceptsTyping(document.activeElement)) {
                return
            }
            if (event.key === "Alt") {
                event.preventDefault()
                RHAltModeManager.shared.enterAltMode()
                return
            }
            if (RHAltModeManager.shared.isActive && event.key === "Escape") {
                RHAltModeManager.shared.exitAltMode()
                return
            }
            if (RHAltModeManager.shared.isActive && !event.ctrlKey && !event.metaKey) {
                // Safely extract character mapping regardless of Alt-mutation (e.g. Alt+A = 'å' on Mac)
                let keyStr = event.key
                if (event.code && event.code.startsWith("Key")) {
                    keyStr = event.code.replace("Key", "")
                }
                else if (event.code && event.code.startsWith("Digit")) {
                    keyStr = event.code.replace("Digit", "")
                }
                
                if (keyStr.length === 1) {
                    event.preventDefault()
                    event.stopPropagation()
                    RHAltModeManager.shared.handleKey(keyStr)
                }
            }
        })
        
        document.addEventListener("keyup", event => {
            if (event.key === "Alt") {
                RHAltModeManager.shared.exitAltMode()
            }
        })
        
        
        const devToggles = {
            layoutDebugger: "dev.UILayoutDebugger.enabled",
            layoutCycleTracer: "dev.UILayoutCycleTracer.enabled",
            socketClientLog: "dev.cbsocketclientlogmessages.enabled"
        } as const
        
        // Restore persisted dev toggle states on startup
        if (localStorage.getItem(devToggles.layoutDebugger) === "1") {
            window.UILayoutDebugger?.enable()
        }
        if (localStorage.getItem(devToggles.layoutCycleTracer) === "1") {
            window.UILayoutCycleTracer?.enable(3)
        }
        if (localStorage.getItem(devToggles.socketClientLog) === "1") {
            // @ts-ignore
            document.cbsocketclientlogmessages = true
        }
        
        document.addEventListener("keydown", event => {
            if (event.ctrlKey && ["e", "w"].contains(event.key)) {
                const isEditorOpen = IS(UIRoute.currentRoute.componentWithName("settings")
                    ?.parameters["editorOpen"])
                if (!isEditorOpen) {
                    UIRoute.currentRoute.routeBySettingParameterInComponent(
                        "settings",
                        "editorOpen",
                        IF(event.key == "e")(() => "YES").ELSE(() => "WINDOW")
                    ).applyByReplacingCurrentRouteInHistory()
                }
                else {
                    UIRoute.currentRoute.routeByRemovingParameterInComponent(
                        "settings",
                        "editorOpen"
                    ).applyByReplacingCurrentRouteInHistory()
                }
            }
            if (event.ctrlKey && ["j"].contains(event.key)) {
                if (!window.UILayoutDebugger?.isEnabled) {
                    window.UILayoutDebugger?.enable()
                    localStorage.setItem(devToggles.layoutDebugger, "1")
                    RHTransientBanner.show(this.view, {
                        message: "Layout debugger ON",
                        verticalPosition: "bottom",
                        horizontalPosition: "center"
                    })
                }
                else {
                    window.UILayoutDebugger?.disable()
                    localStorage.removeItem(devToggles.layoutDebugger)
                    RHTransientBanner.show(this.view, {
                        message: "Layout debugger OFF",
                        verticalPosition: "bottom",
                        horizontalPosition: "center"
                    })
                }
            }
            
            if (event.ctrlKey && ["k"].contains(event.key)) {
                if (!window.UILayoutCycleTracer?.isEnabled) {
                    window.UILayoutCycleTracer?.enable(3)
                    localStorage.setItem(devToggles.layoutCycleTracer, "1")
                    RHTransientBanner.show(this.view, {
                        message: "Layout cycle tracer ON",
                        verticalPosition: "bottom",
                        horizontalPosition: "center"
                    })
                }
                else {
                    window.UILayoutCycleTracer?.disable()
                    localStorage.removeItem(devToggles.layoutCycleTracer)
                    RHTransientBanner.show(this.view, {
                        message: "Layout cycle tracer OFF",
                        verticalPosition: "bottom",
                        horizontalPosition: "center"
                    })
                }
            }
            if (event.ctrlKey && ["l"].contains(event.key)) {
                // @ts-ignore
                document.cbsocketclientlogmessages = !document.cbsocketclientlogmessages
                // @ts-ignore
                if (document.cbsocketclientlogmessages) {
                    localStorage.setItem(devToggles.socketClientLog, "1")
                    RHTransientBanner.show(this.view, {
                        message: "Socket client logging ON",
                        verticalPosition: "bottom",
                        horizontalPosition: "center"
                    })
                }
                else {
                    localStorage.removeItem(devToggles.socketClientLog)
                    RHTransientBanner.show(this.view, {
                        message: "Socket client logging OFF",
                        verticalPosition: "bottom",
                        horizontalPosition: "center"
                    })
                }
            }
            
            
        })
        
        // noinspection JSConstantReassignment
        this.view.controlEventTargetAccumulator.ScrollWheel = (_sender, event) => {
            if (
                UIDialogView._activeDialogCount > 0 ||
                IS(CBDialogViewShower.currentDialogViewShower) ||
                IS(this.detailsViewController)
            ) {
                return
            }
            RHDataView.routePageScrollWheelEventInContentView(
                event as WheelEvent,
                this.contentViewController?.view
            )
        }
        
        
    }
    
    
    async _routeChangeShouldApply(routeChangeDescriptor: UIRouteChangeDescriptor): Promise<boolean> {
        // A logged-out form cannot save, so it must not block the transition to login.
        if (!RHCore.sharedInstance.isUserLoggedIn) {
            return YES
        }
        
        const contentViewController = this.contentViewController
        if (!isRHNavigationResettable(contentViewController)) {
            return YES
        }
        
        if (!this._routeChangeMayResetCurrentContent(routeChangeDescriptor)) {
            return YES
        }
        
        return await contentViewController.navigationWillReset()
    }
    
    
    _routeChangeMayResetCurrentContent(routeChangeDescriptor: UIRouteChangeDescriptor): boolean {
        const contentViewController = this.contentViewController
        if (IS_NOT(contentViewController)) {
            return NO
        }
        
        const currentContentRouteComponentName = contentViewController.class.routeComponentName
        if (!currentContentRouteComponentName) {
            return NO
        }
        
        if (this._modeParameterInRoute(routeChangeDescriptor.currentRoute) !==
            this._modeParameterInRoute(routeChangeDescriptor.targetRoute)) {
            return YES
        }
        
        const currentRouteComponent = routeChangeDescriptor.currentRoute.componentWithName(
            currentContentRouteComponentName
        )
        const targetRouteComponent = routeChangeDescriptor.targetRoute.componentWithName(
            currentContentRouteComponentName
        )
        
        if (IS_NOT(targetRouteComponent)) {
            return YES
        }
        
        if (routeChangeDescriptor.forcefully) {
            return YES
        }
        
        if (IS_NOT(currentRouteComponent)) {
            return YES
        }
        
        return this._routeComponentsHaveDifferentParameters(currentRouteComponent, targetRouteComponent)
    }
    
    
    _routeComponentsHaveDifferentParameters(
        currentRouteComponent: UIRouteComponent,
        targetRouteComponent: UIRouteComponent
    ): boolean {
        const currentParameters = currentRouteComponent.parameters as Record<string, string>
        const targetParameters = targetRouteComponent.parameters as Record<string, string>
        const parameterNames = [
            ...new Set(Object.keys(currentParameters).concat(Object.keys(targetParameters)))
        ]
        
        for (const parameterName of parameterNames) {
            if (currentParameters[parameterName] !== targetParameters[parameterName]) {
                return YES
            }
        }
        
        return NO
    }
    
    
    _modeParameterInRoute(route: UIRoute): string | undefined {
        return route.componentWithName("settings")?.parameters["mode"] as string | undefined
    }


    _routeByApplyingCurrentApplicationMode(route: UIRoute): UIRoute {
        const hasApplicationRole =
            RHCore.sharedInstance._applicationModeIsAvailable("procurer") ||
            RHCore.sharedInstance._applicationModeIsAvailable("offerer") ||
            RHCore.sharedInstance._applicationModeIsAvailable("auditor")

        if (hasApplicationRole) {
            return route.routeBySettingParameterInComponent(
                "settings",
                "mode",
                RHCore.sharedInstance.applicationMode
            )
        }
        return route.routeByRemovingParameterInComponent("settings", "mode")
    }


    // Not a routing rule - a plain access check for whether the current viewer
    // may open a published-amendment procurement route, gated on login state,
    // application mode, and (once known) edit capability. Local to
    // handleRoute's two call sites; no reason for this to be a shared,
    // separately exported "route rule".
    _publishedAmendmentAccessDecision(input: {
        isUserLoggedIn: boolean
        applicationMode: string
        requiredApplicationModes: readonly string[]
        canEdit?: boolean
    }): "deny" | "retrieve-capabilities" | "allow" {
        if (!input.isUserLoggedIn) {
            return "deny"
        }
        if (!input.requiredApplicationModes.includes(input.applicationMode)) {
            return "deny"
        }
        if (input.canEdit === undefined) {
            return "retrieve-capabilities"
        }
        if (!input.canEdit) {
            return "deny"
        }
        return "allow"
    }


    _authenticatedLandingRouteFromRoute(route: UIRoute): UIRoute {
        return authenticatedLandingRouteFromRoute(
            route,
            this.contentViewControllers.allValues.map(
                viewControllerObject => viewControllerObject.class.routeComponentName
            ),
            RHCore.sharedInstance.companyAffiliationStore.affiliations,
            this._authenticationComponentNames,
            this._authenticationLandingComponentName,
            this._authenticationReturnTargetParameterAllowlist
        )
    }


    _templateRouteTargetFromRoute(route: UIRoute): RHAuthenticationTemplateRouteTarget {
        return templateTargetFromRoute(
            route,
            NewProcurementViewController.routeComponentName,
            NewProcurementViewController.ParameterIdentifierName.templateId
        )
    }


    _activeCompanyMatchesTemplateTarget(companyId: string): boolean {
        const affiliationStore = RHCore.sharedInstance.companyAffiliationStore
        return activeCompanyMatches(
            affiliationStore.activeCompanyId,
            affiliationStore.activeCompanyAffiliation,
            companyId
        )
    }


    async _showTemplateRouteTerminalState(
        route: UIRoute,
        routeHandlingIsCurrent: () => boolean,
        message: string
    ) {
        const didReplaceRoute = await this._replaceRouteAfterValidation(
            route
                .routeByRemovingComponentNamed(NewProcurementViewController.routeComponentName)
                .routeWithViewControllerComponent(UserMainViewController, {}),
            route,
            routeHandlingIsCurrent
        )
        if (didReplaceRoute) {
            CBDialogViewShower.alert(message)
        }
    }


    _showTemplateRouteRetry(routeHandlingIsCurrent: () => boolean) {
        if (!routeHandlingIsCurrent()) {
            return
        }
        const shower = CBDialogViewShower.showQuestionDialogWithSupportingText(
            "Hankemalli laadimine ebaõnnestus.",
            "Kontrolli internetiühendust ja proovi uuesti."
        )
        shower.dialogView.view.yesButton.titleLabel.text = "Proovi uuesti"
        shower.dialogView.view.noButton.titleLabel.text = "Sulge"
        shower.dialogView.view.noButtonDismissesDialog = YES
        shower.dialogView.view.setNeedsLayoutUpToRootView()
        shower.yesButtonWasPressed = () => {
            shower.dialogView.dismiss()
            if (routeHandlingIsCurrent()) {
                void this.handleRoute(UIRoute.currentRoute)
            }
        }
    }


    async _prepareTemplateRoute(
        route: UIRoute,
        target: RHAuthenticationTemplateRouteTarget,
        routeHandlingIsCurrent: () => boolean
    ): Promise<boolean> {
        if (target.kind === "invalid") {
            await this._showTemplateRouteTerminalState(
                route,
                routeHandlingIsCurrent,
                "Hankemalli ei leitud."
            )
            return NO
        }
        if (target.kind !== "template") {
            return YES
        }

        const loadingShower = CBDialogViewShower.showActionIndicatorDialog(
            "Hankemalli laadimine..."
        )
        try {
            const result = await RHCore.sharedInstance.procurementStore
                .resolveProcurementTemplateLink(target.templateId)
            if (!routeHandlingIsCurrent()) {
                return NO
            }
            if (!result.success) {
                let message = "Sul puudub õigus seda hankemalli kasutada."
                if (result.errorCode === "TEMPLATE_NOT_FOUND") {
                    message = "Hankemalli ei leitud."
                }
                await this._showTemplateRouteTerminalState(
                    route,
                    routeHandlingIsCurrent,
                    message
                )
                return NO
            }
            if (
                result.value.templateId !== target.templateId ||
                !RHCore.isCandidateIdAValidObjectId(result.value.companyId)
            ) {
                await this._showTemplateRouteTerminalState(
                    route,
                    routeHandlingIsCurrent,
                    "Hankemalli ei leitud."
                )
                return NO
            }
            const affiliationStore = RHCore.sharedInstance.companyAffiliationStore
            let affiliations = await affiliationStore.retrieveMyCompanyAffiliations()
            if (!routeHandlingIsCurrent()) {
                return NO
            }
            let targetAffiliation = directCreateAffiliationForCompany(
                affiliations,
                result.value.companyId
            )
            if (!targetAffiliation) {
                affiliations = await affiliationStore.retrieveMyCompanyAffiliations(YES)
                if (!routeHandlingIsCurrent()) {
                    return NO
                }
                targetAffiliation = directCreateAffiliationForCompany(
                    affiliations,
                    result.value.companyId
                )
            }
            if (!targetAffiliation) {
                await this._showTemplateRouteTerminalState(
                    route,
                    routeHandlingIsCurrent,
                    "Sul puudub õigus seda hankemalli kasutada."
                )
                return NO
            }

            if (affiliationStore.activeCompanyId !== result.value.companyId) {
                affiliationStore.deactivateDefaultAllCompaniesScope()
                affiliationStore.activateCompany(result.value.companyId)
                if (!this._activeCompanyMatchesTemplateTarget(result.value.companyId)) {
                    await this._showTemplateRouteTerminalState(
                        route,
                        routeHandlingIsCurrent,
                        "Ettevõtte konteksti vahetamine ebaõnnestus."
                    )
                }
                // A successful switch broadcasts applicationModeDidChange.
                // That canonical reconciliation pass performs the mount.
                return NO
            }
            if (!this._activeCompanyMatchesTemplateTarget(result.value.companyId)) {
                await this._showTemplateRouteTerminalState(
                    route,
                    routeHandlingIsCurrent,
                    "Sul puudub õigus seda hankemalli kasutada."
                )
                return NO
            }

            const modeParameter = route.componentWithName("settings")?.parameters["mode"]
            if (modeParameter !== "procurer") {
                await this._replaceRouteAfterValidation(
                    route.routeBySettingParameterInComponent("settings", "mode", "procurer"),
                    route,
                    routeHandlingIsCurrent
                )
                return NO
            }
            return YES
        }
        catch (error) {
            console.error("RootViewController: template route resolution failed", error)
            this._showTemplateRouteRetry(routeHandlingIsCurrent)
            return NO
        }
        finally {
            if (CBDialogViewShower.currentActionIndicatorDialogViewShower === loadingShower) {
                CBDialogViewShower.hideActionIndicatorDialog()
            }
        }
    }


    async _applyRouteOrHandleCurrentRoute(
        targetRoute: UIRoute,
        reconciliationGeneration: number
    ) {
        const routeReconciliationIsCurrent = () =>
            reconciliationGeneration === this._routeReconciliationGeneration
        if (!routeReconciliationIsCurrent()) {
            return
        }

        const currentRoute = UIRoute.currentRoute
        if (currentRoute.stringRepresentation === targetRoute.stringRepresentation) {
            await this.handleRoute(targetRoute)
            return
        }

        await this._replaceRouteAfterValidation(
            targetRoute,
            currentRoute,
            routeReconciliationIsCurrent
        )
    }


    async _replaceRouteAfterValidation(
        targetRoute: UIRoute,
        currentRoute: UIRoute,
        routeTransitionIsCurrent: () => boolean
    ): Promise<boolean> {
        if (!routeTransitionIsCurrent()) {
            return NO
        }

        const routeChangeDescriptor: UIRouteChangeDescriptor = {
            currentRoute,
            targetRoute: targetRoute.copy(),
            forcefully: NO,
            replacesCurrentRouteInHistory: YES
        }
        const routeChangeShouldApply = await this._routeChangeShouldApply(routeChangeDescriptor)
        if (
            !routeChangeShouldApply ||
            !routeTransitionIsCurrent() ||
            currentRoute.stringRepresentation !== UIRoute.currentRoute.stringRepresentation
        ) {
            return NO
        }

        return targetRoute.applyByReplacingCurrentRouteInHistoryWithoutRouteChangeApproval()
    }


    _showPublishedAmendmentAccessDenialBeforeReplacingRoute(
        procurementDetailsRoute: UIRoute,
        amendmentRoute: UIRoute
    ) {
        const amendmentRouteString = amendmentRoute.stringRepresentation
        if (this._publishedAmendmentAccessDenialRouteString === amendmentRouteString) {
            return
        }

        this._publishedAmendmentAccessDenialRouteString = amendmentRouteString
        const denialDialogShower = CBDialogViewShower.alert(
            "Sul puudub selle hanke muutmise õigus.",
            () => {
                if (this._publishedAmendmentAccessDenialDialogShower === denialDialogShower) {
                    this._publishedAmendmentAccessDenialDialogShower = undefined
                }
                if (this._publishedAmendmentAccessDenialRouteString !== amendmentRouteString) {
                    return
                }
                this._publishedAmendmentAccessDenialRouteString = undefined
                this._replaceRouteAfterValidation(
                    procurementDetailsRoute,
                    amendmentRoute,
                    () => UIRoute.currentRoute.stringRepresentation === amendmentRouteString
                ).then(undefined)
            }
        )
        this._publishedAmendmentAccessDenialDialogShower = denialDialogShower
    }


    async _reconcileAuthenticatedRoute() {
        const reconciliationGeneration = ++this._routeReconciliationGeneration
        await RHCore.sharedInstance.currentCompanyAffiliation
        if (
            reconciliationGeneration !== this._routeReconciliationGeneration ||
            !RHCore.sharedInstance.isUserLoggedIn
        ) {
            return
        }

        let targetRoute = UIRoute.currentRoute
        targetRoute = this._routeByApplyingCurrentApplicationMode(targetRoute)
        if (routeContainsAuthenticationComponent(targetRoute, this._authenticationComponentNames)) {
            targetRoute = this._authenticatedLandingRouteFromRoute(targetRoute)
        }
        await this._applyRouteOrHandleCurrentRoute(targetRoute, reconciliationGeneration)
    }
    
    
    _telemetryURLForRoute(route: UIRoute): string {
        
        let telemetryRoute = route
        
        if (route.componentWithViewController(ResetPasswordViewController)) {
            telemetryRoute = telemetryRoute.routeByRemovingParameterInComponent(
                ResetPasswordViewController.routeComponentName,
                ResetPasswordViewController.ParameterIdentifierName.token
            )
            telemetryRoute = telemetryRoute.routeByRemovingParameterInComponent(
                ResetPasswordViewController.routeComponentName,
                ResetPasswordViewController.ParameterIdentifierName.invitationToken
            )
        }

        if (route.componentWithViewController(CompanyRelationshipApprovalViewController)) {
            telemetryRoute = telemetryRoute.routeByRemovingParameterInComponent(
                CompanyRelationshipApprovalViewController.routeComponentName,
                CompanyRelationshipApprovalViewController.ParameterIdentifierName.token
            )
        }

        return window.location.origin +
            window.location.pathname +
            window.location.search +
            telemetryRoute.linkRepresentation
        
    }
    
    
    override async viewDidAppear() {
        
        await super.viewDidAppear()
        
        this.topBarView.setNeedsLayout()
        
        this._showActiveDevToggleBanners()
        
        // await new Promise((resolve: any) => setTimeout(resolve, 1000))
        // new InvitedCompaniesDialogView().show("34110000-1")
        
    }
    
    
    private _showActiveDevToggleBanners() {
        
        const activeMessages: string[] = []
        
        if (window.UILayoutDebugger?.isEnabled) {
            activeMessages.push("Layout debugger ON")
        }
        if (window.UILayoutCycleTracer?.isEnabled) {
            activeMessages.push("Layout cycle tracer ON")
        }
        // @ts-ignore
        if (document.cbsocketclientlogmessages) {
            activeMessages.push("Socket client logging ON")
        }
        
        activeMessages.forEach(message => {
            RHTransientBanner.show(this.view, {
                message,
                verticalPosition: "bottom",
                horizontalPosition: "center"
            })
        })
        
    }
    
    
    
    override async handleRoute(route: UIRoute) {

        const routeHandlingGeneration = ++this._routeHandlingGeneration
        const routeHandlingIsCurrent = () =>
            routeHandlingGeneration === this._routeHandlingGeneration &&
            route.stringRepresentation === UIRoute.currentRoute.stringRepresentation

        if (
            this._publishedAmendmentAccessDenialRouteString &&
            this._publishedAmendmentAccessDenialRouteString !== route.stringRepresentation
        ) {
            this._publishedAmendmentAccessDenialRouteString = undefined
            this._publishedAmendmentAccessDenialDialogShower?.dismiss()
            this._publishedAmendmentAccessDenialDialogShower = undefined
        }
        
        const legacyStandaloneProcurerRouteComponent = route.componentWithName("procurer_dashboard")
        if (IS(legacyStandaloneProcurerRouteComponent)) {
            await this._replaceRouteAfterValidation(
                route
                    .routeByRemovingComponentNamed("procurer_dashboard")
                    .routeWithViewControllerComponent(
                        UserMainViewController,
                        legacyStandaloneProcurerRouteComponent.parameters
                    ),
                route,
                routeHandlingIsCurrent
            )
            return
        }

        // Resolve the initial authentication state before choosing a landing
        // controller. Otherwise a valid logged-in navigation can briefly take
        // the login fallback while the session is still being restored.
        await RHCore.sharedInstance.sessionInitialised
        if (!routeHandlingIsCurrent()) {
            return
        }

        // A mailed temporary password must be replaced before anything else is
        // reachable - the backend refuses every other socket message while this
        // is set (see SocketSession.keysAllowedWhilePasswordChangeIsRequired), so
        // routing anywhere but the profile screen would just surface as errors.
        if (
            RHCore.sharedInstance.isUserLoggedIn &&
            RHCore.sharedInstance.userProfile?.isPasswordChangeRequired &&
            !route.componentWithViewController(UserProfileViewController)
        ) {
            await this._replaceRouteAfterValidation(
                route
                    .routeByRemovingComponentsOtherThanOnesNamed(["settings"])
                    .routeWithViewControllerComponent(UserProfileViewController, {}),
                route,
                routeHandlingIsCurrent
            )
            return
        }

        // OAuth users who signed in without a name in their provider profile must
        // complete their name before navigating elsewhere.
        const contactInformation = RHCore.sharedInstance.userProfile?.contactInformation
        if (
            RHCore.sharedInstance.isUserLoggedIn &&
            (!contactInformation?.firstName || !contactInformation?.lastName) &&
            !route.componentWithViewController(UserProfileViewController)
        ) {
            await this._replaceRouteAfterValidation(
                route
                    .routeByRemovingComponentsOtherThanOnesNamed(["settings"])
                    .routeWithViewControllerComponent(UserProfileViewController, {}),
                route,
                routeHandlingIsCurrent
            )
            return
        }

        if (
            RHCore.sharedInstance.isUserLoggedIn &&
            routeContainsAuthenticationComponent(route, this._authenticationComponentNames)
        ) {
            await this._replaceRouteAfterValidation(
                this._authenticatedLandingRouteFromRoute(
                    this._routeByApplyingCurrentApplicationMode(route)
                ),
                route,
                routeHandlingIsCurrent
            )
            return
        }

        // A copied deep link carries its sender's route mode. The recipient's
        // per-company preference is the actual active mode for this access
        // decision, so a dual-role offerer is never switched into the private
        // procurer editor merely by opening someone else's URL.
        const preferredApplicationMode = RHCore.sharedInstance.preferredApplicationMode
        let procurementDetailsRoute =
            procurementDetailsRouteFromPublishedAmendmentRouteWithApplicationMode(
                route,
                NewProcurementViewController.routeComponentName,
                ProcurementDetailsViewController.routeComponentName,
                preferredApplicationMode,
                this._authenticationLandingComponentName
            )
        if (procurementDetailsRoute) {
            let accessDecision = this._publishedAmendmentAccessDecision({
                isUserLoggedIn: RHCore.sharedInstance.isUserLoggedIn,
                applicationMode: preferredApplicationMode,
                requiredApplicationModes: NewProcurementViewController.requiredApplicationModes
            })
            if (accessDecision === "retrieve-capabilities") {
                const procurementId = procurementDetailsRoute
                    .componentWithViewController(ProcurementDetailsViewController)
                    ?.parameters.id
                if (!procurementId) {
                    return
                }
                try {
                    const capabilities = await RHCore.sharedInstance.procurementStore
                        .retrieveProcurementCapabilities(procurementId)
                    if (!routeHandlingIsCurrent()) {
                        return
                    }
                    accessDecision = this._publishedAmendmentAccessDecision({
                        isUserLoggedIn: YES,
                        applicationMode: preferredApplicationMode,
                        requiredApplicationModes: NewProcurementViewController.requiredApplicationModes,
                        canEdit: capabilities.canEdit
                    })
                }
                catch (error) {
                    console.error("Published amendment access check failed:", error)
                    CBDialogViewShower.alert("Hanke muutmisõiguse kontrollimine ebaõnnestus.")
                    return
                }
            }
            if (accessDecision === "deny") {
                if (!RHCore.sharedInstance.isUserLoggedIn) {
                    procurementDetailsRoute = procurementDetailsRoute
                        .routeByRemovingParameterInComponent("settings", "mode")
                }
                this._showPublishedAmendmentAccessDenialBeforeReplacingRoute(
                    procurementDetailsRoute,
                    route
                )
                return
            }
        }

        const templateRouteTarget = this._templateRouteTargetFromRoute(route)
        // A company workflow link (invitation/join-request accept) is meaningless while
        // logged out: CompanyViewController resolves it against the caller's own company
        // affiliations, which are empty pre-login, so the link would just land on an empty
        // "no company" screen instead of the invitation. Force login first, same as a
        // logged-out procurement template link below.
        const requiresLoginForCompanyWorkflowLink =
            !!CompanyViewController.workflowIdInRoute(route)
        if (
            (templateRouteTarget.kind !== "none" || requiresLoginForCompanyWorkflowLink) &&
            !RHCore.sharedInstance.isUserLoggedIn &&
            !routeContainsAuthenticationComponent(route, this._authenticationComponentNames)
        ) {
            await this._replaceRouteAfterValidation(
                route.routeWithViewControllerComponent(LoginViewController, {}),
                route,
                routeHandlingIsCurrent
            )
            return
        }
        if (
            RHCore.sharedInstance.isUserLoggedIn &&
            !await this._prepareTemplateRoute(
                route,
                templateRouteTarget,
                routeHandlingIsCurrent
            )
        ) {
            return
        }
        
        const contentViewControllerObject = await this.contentViewControllers.allValues.findAsyncSequential(
            async value => IS(route.componentWithViewController(value.class)) && await value.shouldShow()
        )
        if (!routeHandlingIsCurrent()) {
            return
        }
        if (!contentViewControllerObject) {
            if (RHCore.sharedInstance.isUserLoggedIn) {
                await this._replaceRouteAfterValidation(
                    route.routeWithViewControllerComponent(UserMainViewController, {}),
                    route,
                    routeHandlingIsCurrent
                )
                return
            }

            await this._replaceRouteAfterValidation(
                route.routeWithViewControllerComponent(LoginViewController, {}),
                route,
                routeHandlingIsCurrent
            )
            return
        }
        
        const requiredModes: RHApplicationMode[] | null | undefined =
            contentViewControllerObject.class.requiredApplicationModes
        if (
            requiredModes &&
            !requiredModes.contains(RHCore.sharedInstance.applicationMode)
        ) {
            await this._replaceRouteAfterValidation(
                route
                    .routeByRemovingComponentNamed(
                        contentViewControllerObject.class.routeComponentName
                    )
                    .routeWithViewControllerComponent(UserMainViewController, {}),
                route,
                routeHandlingIsCurrent
            )
            return
        }
        
        // Enforce the mode parameter against the matching company role. Every
        // affiliated role-holder keeps an explicit mode in the route.
        const modeParameterValue = route.componentWithName(
            "settings")?.parameters["mode"] as RHApplicationMode | undefined
        await RHCore.sharedInstance.currentCompanyAffiliation
        if (!routeHandlingIsCurrent()) {
            return
        }
        if (modeParameterValue === "procurer" || modeParameterValue === "offerer" || modeParameterValue === "auditor") {
            const canUseMode = RHCore.sharedInstance._applicationModeIsAvailable(modeParameterValue)
            if (canUseMode) {
                RHCore.sharedInstance.applicationMode = modeParameterValue
            }
            else {
                // User manually added an unavailable mode — strip it.
                await this._replaceRouteAfterValidation(
                    route.routeByRemovingParameterInComponent("settings", "mode"),
                    route,
                    routeHandlingIsCurrent
                )
                return
            }
        }
        else if (
            RHCore.sharedInstance._applicationModeIsAvailable("procurer") ||
            RHCore.sharedInstance._applicationModeIsAvailable("offerer") ||
            RHCore.sharedInstance._applicationModeIsAvailable("auditor")
        ) {
            // User manually removed the mode — restore it from localStorage/default.
            await this._replaceRouteAfterValidation(
                route.routeBySettingParameterInComponent(
                    "settings",
                    "mode",
                    RHCore.sharedInstance.applicationMode
                ),
                route,
                routeHandlingIsCurrent
            )
            return
        }
        
        await super.handleRoute(route)
        if (!routeHandlingIsCurrent()) {
            return
        }
        this._applyContentViewChrome()
        
        // @ts-ignore
        LanguageService.updateCurrentLanguageKey(route)
        
        const currentURL = this._telemetryURLForRoute(route)
        if (IS(currentURL)) {
            RHUsageEventRuntime.routeDidChange(currentURL)
        }
        const editorParameterValue: string = route.componentWithName("settings")?.parameters["editorOpen"]
        if (editorParameterValue) {
            this.showEditor(editorParameterValue.toUpperCase() == "WINDOW")
        }
        else {
            this.hideEditor()
        }
        
        this.topBarView.titleLabel.localizedTextObject = LanguageService.localizedTextObjectForKey(
            titleKeyOfViewController(this.contentViewController) ?? "topBarTitle",
            LanguageService.stringForKey("topBarTitle", LanguageService.currentLanguageKey, "Top bar title")
        )
        
    }
    
    
    override viewDidReceiveBroadcastEvent(event: UIViewBroadcastEvent) {
        
        super.viewDidReceiveBroadcastEvent(event)
        
        const rerouteEventNames: string[] = [
            RHCore.broadcastEventName.userDidLogIn,
            RHCore.broadcastEventName.userDidLogOut,
            RHCore.broadcastEventName.currentCompanyAffiliationDidChange,
            RHCore.broadcastEventName.applicationModeDidChange
        ]
        if (rerouteEventNames.contains(event.name)) {
            if (event.name == RHCore.broadcastEventName.userDidLogOut) {
                const reconciliationGeneration = ++this._routeReconciliationGeneration
                const allLazyObjects = [
                    ...this.contentViewControllers.allValues,
                    ...this.detailsViewControllers.allValues
                ]
                for (const lazyObject of allLazyObjects) {
                    if (lazyObject.deleteOnLogout) {
                        lazyObject.deleteInstance()
                    }
                }
                this._applyRouteOrHandleCurrentRoute(
                    UIRoute.currentRoute.routeByRemovingParameterInComponent(
                        "settings",
                        "mode",
                        YES
                    ),
                    reconciliationGeneration
                ).catch(error => {
                    console.error("RootViewController: logout route reconciliation failed", error)
                })
            }
            else {
                this._reconcileAuthenticatedRoute().catch(error => {
                    console.error("RootViewController: authenticated route reconciliation failed", error)
                })
            }
        }
        
        if (event.name == UIView.broadcastEventName.LanguageChanged) {
            this.detailsViewController = nil
            this._detailsDialogView.dismiss()
            this._triggerLayoutViewSubviews()
        }
        
        if (event.name == RHCore.broadcastEventName.newBuildAvailable) {
            if (!this._updateBannerView) {
                this._updateBannerView = new UITextView("UpdateBannerView").configuredWithObject({
                    text: "Rakenduse uus versioon on saadaval. Palun laadi leht uuesti.",
                    textAlignment: UITextView.textAlignment.center,
                    backgroundColor: new UIColor("darkorange"),
                    textColor: UIColor.whiteColor,
                    style: { fontWeight: "600", fontSize: "13px" }
                })
                this._updateBannerView.addedAsSubviewToView(this.view)
                this._triggerLayoutViewSubviews()
            }
        }

    }
    
    async showEditor(separateWindow: boolean) {
        
        if (separateWindow) {
            
            // @ts-ignore
            this.editorWindow = window.open(
                window.location.origin + "/#cb_editor[]",
                "CBEditorWindow",
                "popup"
            )
            
            if (this.editorWindow) {
                
                // @ts-ignore
                this.editor = this.editorWindow.editorViewController
                
                // @ts-ignore
                const screenDetails = await window.getScreenDetails?.()
                
                // @ts-ignore
                if (window.screen.isExtended && screenDetails?.screens?.length > 1) {
                    const secondScreen: ScreenDetail | undefined = screenDetails.screens.find((screen: ScreenDetail) => !screen.isPrimary)
                    if (secondScreen) {
                        this.editorWindow.resizeTo(secondScreen.availWidth, secondScreen.availHeight)
                        this.editorWindow.moveTo(secondScreen.availLeft, secondScreen.availTop)
                    }
                }
                
                const timer = setInterval(async () => {
                    if (this.editorWindow?.closed ?? true) {
                        clearInterval(timer)
                        UIRoute.currentRoute
                            .routeByRemovingParameterInComponent("settings", "editorOpen")
                            .apply()
                        await SocketClient.EditorWasClosed()
                    }
                }, 100)
                
            }
            
        }
        else {
            
            if (IS_NOT(this.editor)) {
                this.editor = new EditorViewController(new UINativeScrollView("CBEditorView"))
            }
            
            this.editor.view.pointerDraggingPoint = new UIPoint(0, 0)
            this.editor?.viewWillAppear()
            this.editor?.view.willAppear()
            this.addChildViewController(this.editor)
            this.editor?.viewDidAppear()
            
        }
        
    }
    
    hideEditor() {
        
        if (this.editorWindow) {
            this.editorWindow?.close()
            this.editorWindow = undefined
            this.editor = undefined
        }
        else {
            this.editor?.viewWillDisappear()
            this.editor?.removeFromParentViewController()
            this.editor?.viewDidDisappear()
            this.editor = undefined
        }
        
    }
    
    
    override updateViewStyles() {
    
    }
    
    override viewDidLayoutSubviews() {
        super.viewDidLayoutSubviews()
    }
    
    private _applyContentViewChrome() {
        
        if (IS(this.contentViewController)) {
            this.contentViewController.view.style.boxShadow = "none"
        }
        
    }
    
    
    get _breadcrumbItems(): RHBreadcrumbItem[] {
        const controller = this.contentViewController
        if (!controller || controller instanceof ProcurementDetailsViewController || controller instanceof SettingsViewController) {
            return []
        }
        if (controller instanceof UserMainViewController || controller instanceof NewProcurementViewController) {
            return controller.breadcrumbItems
        }
        const route = UIRoute.currentRoute
        const baseRoute = route.routeByRemovingComponentsOtherThanOnesNamed(["settings"])
        if (controller instanceof CompanyViewController || controller instanceof UserProfileViewController) {
            return SettingsViewController.breadcrumbItemsForRoute(route)
        }
        if (controller instanceof NewProcurementPreviewViewController) {
            return [
                { title: "Töölaud", route: baseRoute.routeWithViewControllerComponent(UserMainViewController, { tab: "published" }) },
                { title: "Ettevalmistamisel ja mallid", route: baseRoute.routeWithViewControllerComponent(UserMainViewController, { tab: "drafts" }) },
                { title: RHTextSanitizer.plainText(controller.titleLabel.text) }
            ]
        }
        if (controller instanceof LogViewController) {
            const tabButton = IF(controller.sectionTabGroup.value === "eventLog")(() =>
                controller.myEventsTabButton
            ).ELSE(() => controller.notificationsTabButton)
            return [
                { title: "Teavitused", route: baseRoute.routeWithViewControllerComponent(LogViewController, { tab: "notifications" }) },
                { title: RHTextSanitizer.plainText(tabButton.titleLabel.text) }
            ]
        }
        if (controller instanceof RHRStatisticsViewController || controller instanceof RHRStatisticsComparisonViewController) {
            let title = "Hankija statistika"
            if (controller.entityType === "offerer") {
                title = "Pakkuja statistika"
            }
            if (controller instanceof RHRStatisticsComparisonViewController) {
                title = IF(controller.entityType === "offerer")(() => "Pakkujate võrdlus").ELSE(() => "Hankijate võrdlus")
            }
            return [
                { title: "Avalik statistika", route: baseRoute.routeWithViewControllerComponent(StatisticsMainViewController, {}) },
                { title }
            ]
        }
        const navigationButton = this.topBarView.navigationButtonGroup.buttonsMap[controller.class.routeComponentName]
        if (navigationButton instanceof RHBarLinkButton) {
            return [{ title: RHTextSanitizer.plainText(navigationButton.titleLabel.text) }]
        }
        const pageTitles: Record<string, string> = {
            [HelpViewController.routeComponentName]: "Abi",
            [InformationViewController.routeComponentName]: "Info",
            [ProcurementPlanViewController.routeComponentName]: "Hankeplaan",
            [LoginViewController.routeComponentName]: "Logi sisse",
            [RegisterUserViewController.routeComponentName]: "Registreeru",
            [ForgotPasswordViewController.routeComponentName]: "Parooli taastamine",
            [ResetPasswordViewController.routeComponentName]: "Parooli muutmine",
            [InternalDropdownSettingsViewController.routeComponentName]: "Valikute seaded",
            [InternalLanguageSettingsViewController.routeComponentName]: "Tõlked",
            [InternalUsageDataViewController.routeComponentName]: "Kasutusandmed",
            [SystemAdministrationViewController.routeComponentName]: "Süsteemi haldus"
        }
        const pageTitle = pageTitles[controller.class.routeComponentName]
        if (pageTitle) {
            return [{ title: pageTitle }]
        }
        if ("titleLabel" in controller && controller.titleLabel instanceof UITextView) {
            return [{ title: RHTextSanitizer.plainText(controller.titleLabel.text) }]
        }
        return []
    }


    override layoutViewSubviews() {
        
        super.layoutViewSubviews()
        
        // Down-scale the UI on smaller screens so it is not cramped, keyed on the available
        // CSS width (updatePageScale reads window.innerWidth): ≥2560 → 1.0, ≤1920 → 0.8, linear
        // between, floored at 0.8. This is CSS width by design — a high-DPI screen (e.g. a 2560
        // monitor at 150% OS scaling → CSS width ~1707) scales down too, because what makes the
        // UI feel cramped is the CSS layout space, not the raw pixel count.
        this.updatePageScale({
            minScaleWidth: 1920,
            maxScaleWidth: 2560,
            minScale: 0.8,
            maxScale: 1
        })
        
        // The setter fills the root to the exact pixel edge: it sizes #RootView to
        // width:100/scale% and transform:scale(scale)s it back to 100% of the viewport.
        // But uicore lays the full-width chrome (top/bottom bars) out to a WHOLE-pixel
        // width, and when the viewport is not a whole multiple of 1/scale that rounded
        // width, scaled back, lands a fraction of a pixel PAST the viewport edge. A 2560
        // screen at 150% OS scaling gives CSS width 1707, and round(1707/0.8)·0.8 = 2134·0.8
        // = 1707.2 — 0.2px over. On displays with classic (space-consuming) scrollbars that
        // fractional overshoot trips both scrollbars; on "clean" widths (1920→2400, 1600→2000)
        // there is no fraction and no bug. Snap the scale so the LAYOUT width (clientWidth,
        // i.e. excluding any scrollbar already present) divides into a whole number of pixels,
        // landing the scaled footprint exactly on the edge. No-op on clean widths.
        const layoutWidth = document.documentElement.clientWidth
        if (UIView.pageScale < 1 && layoutWidth > 0) {
            UIView.pageScale = layoutWidth / Math.round(layoutWidth / UIView.pageScale)
        }
        
        // pageScale compensates width but NOT height, so on SHORT pages (e.g. login) the
        // scaled root would fill only `scale`× the viewport height, leaving a blank gap and
        // floating the footer. Fill the height the same way — but in FLOORED whole pixels, so
        // scaling it back never rounds past the bottom edge (the vertical twin of the width
        // overshoot above). Tall content still grows past it and scrolls normally.
        this.view.style.minHeight = Math.floor(document.documentElement.clientHeight / UIView.pageScale) + "px"
        
        const topBarHeight = this.topBarView.intrinsicContentHeight(UIView.pageWidth) || 65
        const bannerHeight = IF(IS(this._updateBannerView))(() =>
            this._updateBannerView!.intrinsicContentHeight(UIView.pageWidth)
        ).ELSE(() => 0)

        const breadcrumbItems = this._breadcrumbItems
        this.breadcrumbView.items = breadcrumbItems
        if (breadcrumbItems.length) {
            this.breadcrumbView.addedAsSubviewToView(this.view)
        }
        else {
            this.breadcrumbView.removeFromSuperview()
        }
        const breadcrumbBounds = this.view.contentBoundsWithInset(this.core.paddingLength)
            .rectangleWithY(topBarHeight + bannerHeight + this.core.paddingLength)
            .rectangleWithHeight(this.breadcrumbView)
        const breadcrumbHeight = IF(breadcrumbItems.length > 0)(() =>
            breadcrumbBounds.height + this.core.paddingLength
        ).ELSE(() => 0)
        
        if (IS(this._updateBannerView)) {
            this._updateBannerView!.frame = this.view.bounds.rectangleWithHeight(bannerHeight)
        }
        
        this.performDefaultLayout({
            paddingLength: this.core.paddingLength,
            contentViewMaxWidth: 15000,
            topBarHeight: topBarHeight + bannerHeight + breadcrumbHeight,
            bottomBarMinHeight: 100
        })
        
        this.topBarView.frame = this.topBarView.frame.rectangleWithY(bannerHeight).rectangleWithHeight(topBarHeight)
        if (breadcrumbItems.length) {
            breadcrumbBounds.assignedAsFrameOfView(this.breadcrumbView)
        }
        
        this.contentViewController?.view.setNeedsLayout()
        
    }
    
}
